<template>
  <div
    class="text-static-default-low body-1 relative grid gap-x-4 text-left [grid-auto-columns:repeat(2,1fr)]"
    :style="{ 'grid-template-areas': gridTemplateAreasClasses }"
  >
    <div class="bg-surface-default-hi rounded-t-md grid p-8 [grid-area:title1]">
      <h2 class="text-static-default-hi body-1-bold">
        {{ i18n(translations.item1Title) }}
      </h2>
    </div>
    <div
      class="bg-static-ai-mid rounded-t-md relative flex items-center overflow-hidden p-8 [grid-area:title2] after:absolute after:top-0 after:h-full after:w-full after:-translate-x-full after:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.6),transparent)] after:content-['']"
      :class="$style.animateShimmer"
    >
      <h2 class="text-static-brand-hi body-1-bold">
        {{ i18n(translations.item2Title) }}
      </h2>
    </div>
    <div
      v-for="(item, index) in currentOffer"
      :key="item && 'label' in item ? item?.label : index"
      class="bg-surface-default-mid body-2 px-8 py-6"
      :style="{
        'grid-area': `current-text${index + 1}`,
      }"
    >
      <div
        v-if="item && 'label' in item"
        class="text-static-default-low flex h-full items-center"
      >
        <div class="flex">
          <RevColorSwatch v-if="item.color" class="mr-4" :color="item.color" />
          <span>
            {{ item?.label }}
          </span>
        </div>
      </div>
    </div>
    <!-- Price for the selected offer is the same regardless the step -->
    <div
      v-if="
        currentOffer[0] && 'price' in currentOffer[0] && currentOffer[0]?.price
      "
      class="bg-surface-default-mid text-static-default-mid rounded-b-md heading-2 px-8 pb-8 pt-4"
      :style="{
        'grid-area': `current-text${currentOffer.length + 1}`,
      }"
    >
      {{ i18n.price(currentOffer[0].price) }}
    </div>
    <div
      v-for="(item, index) in upgradeOffer"
      :key="item.value"
      class="bg-static-ai-low px-8 py-6"
      :style="{
        'grid-area': `upgrade-text${index + 1}`,
      }"
    >
      <div
        class="flex h-full items-center justify-between"
        :class="{
          'text-static-brand-hi body-2-bold': item.upgrade,
          'text-static-default-hi body-2': !item.upgrade,
        }"
      >
        <div class="flex">
          <RevColorSwatch v-if="item.color" class="mr-4" :color="item.color" />
          <span>
            {{ item.value }}
          </span>
        </div>
        <IconCheckWithSparkles v-if="item.upgrade" />
      </div>
    </div>
    <!-- Price for the selected offer is the same regardless the step -->
    <div
      v-if="pickers.upgrade?.price"
      class="bg-static-ai-low rounded-b-md px-8 pb-8 pt-4"
      :style="{
        'grid-area': `upgrade-text${upgradeOffer.length + 1}`,
      }"
    >
      <div class="text-static-default-mid heading-2">
        {{ i18n.price(pickers.upgrade.price) }}
      </div>
      <RevTag
        v-if="amountSaved"
        class="text-wrap"
        :label="
          i18n(translations.youSave, {
            amount: i18n.price({
              amount: amountSaved.toString(),
              currency: pickers.upgrade.price.currency,
            }),
          })
        "
        variant="success"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevColorSwatch } from '@ds/components/ColorSwatch'
import { RevTag } from '@ds/components/Tag'
import { IconCheckWithSparkles } from '@ds/icons/IconCheckWithSparkles'

import translations from './UpgradeComparisonTable.translations'

const props = defineProps<{
  pickers: GetPickersResponse
}>()

const i18n = useI18n()

const currentOffer = computed(() => {
  return props.pickers?.pickerGroups.map((pickerGroup) =>
    pickerGroup.items.find((item) => item.selected),
  )
})

const upgradeOffer = computed(() => {
  const { upgrade } = props.pickers

  if (upgrade) {
    const pickerFieldKeys = Object.keys(upgrade.pickerFields)
    const pickerFieldValues = Object.values(upgrade.pickerFields)

    // Since the order of the pickers is managed by pickerGroups, the order of upgrade offer should be too.
    return props.pickers?.pickerGroups.reduce(
      (acc, pickerGroup) => {
        const fieldIndex = pickerFieldKeys.findIndex(
          (key) => key === pickerGroup.id,
        )
        if (fieldIndex !== -1) {
          acc.push(pickerFieldValues[fieldIndex])
        }

        return acc
      },
      [] as unknown as Array<{
        color?: string | null
        value?: string
        upgrade?: boolean
        label?: string | null
      }>,
    )
  }

  return []
})

const gridTemplateAreasClasses = computed(() => {
  const texts = upgradeOffer.value.map((_offer, index) => {
    return `"current-text${index + 1} upgrade-text${index + 1}"`
  })

  const prices = `"current-text${upgradeOffer.value.length + 1} upgrade-text${upgradeOffer.value.length + 1}"`

  return `"title1 title2" ${texts.join(' ')} ${prices}`
})

// Price for the selected offer is the same regardless the step
const amountSaved = computed(() => {
  return currentOffer.value[0] &&
    'price' in currentOffer.value[0] &&
    currentOffer.value[0]?.price &&
    props.pickers.upgrade?.price
    ? parseInt(currentOffer.value[0].price.amount, 10) -
        parseInt(props.pickers.upgrade.price.amount, 10)
    : undefined
})
</script>

<style module lang="css" scoped>
.animateShimmer::after {
  animation: shimmer-wave 1500ms linear forwards;
}

@keyframes shimmer-wave {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(100%);
  }
}
</style>
